import { FC, useState, useEffect, useContext, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Drawer,
  OutlinedInput,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  useTheme,
} from '@mui/material'
import { SelectComponent } from '../../../shared/SelectComponent'
import {
  getSfafAgencyOverride,
  getAllHopsets,
  getAllSFAFAntennas,
  getAllSFAFContents,
  getAllSFAFEquipments,
  getAllSFAFExcludedFrequencies,
  getAllSFAFFrequencies,
  getAllSFAFStationClasses,
  getFrequenciesByFrequencyGroupId,
  getEquipmentsByFrequencyGroupId,
  getFrequencyGroup,
  updateFrequency,
  updateEquipment,
  updateRequestStatus,
  updateFrequencyGroup,
} from '../../../../services/spectrum-manager.service'
import { IRequestFrequencyGroup } from '../../../../types/exercise.type'
import { IRequestEquipment } from '../../../../types/equipment.type'
import { IFrequency } from '../../../../types/spectrum-manager.type'
import { ISpecManRequestFrequency } from '../../../../../shared/types/api/spectrum-manager.type'
import RFAExportModal from '../../Modals/RfaExportModal'
import { downloadPdf } from '../../../../services/playbook.service'
import { generateSFAF } from '../../../../SFAF/sfaf-generator'
import { isValidBase64, findEmissionCodeDefaultValue } from '../../../../utils/functions'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'

import styles from './styles.module.scss'

interface IProps {
  isOpen: boolean
  request: ISpecManRequestFrequency
  isEditing?: boolean
  handleClose: () => void
}

const SFAFDrawer: FC<IProps> = ({ isOpen, request, handleClose }) => {
  const theme = useTheme()

  const [sfaf, setSfaf] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [frequencyGroup, setFrequencyGroup] = useState<IRequestFrequencyGroup | null>(null)
  const [equipments, setEquipments] = useState<IRequestEquipment[]>([])
  const [frequencies, setFrequencies] = useState<Array<IFrequency & { frequency_indicator: string }>>([])

  const { filterOptions, setDetailedRequests } = useContext(SpectrumManagerContext)

  const pdfRef = useRef<HTMLAnchorElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)

  const onClose = () => {
    handleClose()
  }

  const handleExport = () => {
    if (linkRef.current && sfaf) {
      const file = new Blob([sfaf], { type: 'text/plain' })
      linkRef.current.href = URL.createObjectURL(file)
      linkRef.current.download = 'sfaf.txt'

      linkRef.current.click()
    }
  }

  /*
    TODO: This function is triggered immediately after the user modifies the inputs. 
    Consider adding debouncing.
  */
  const handleChange = async (type: string, id: number, key: string, value: string | number) => {
    if (type === 'freq') {
      const updatedFrequencies = frequencies.map((freq) =>
        id === freq.id
          ? {
              ...freq,
              [key]: value,
            }
          : freq,
      )

      const updatedFrequency = updatedFrequencies.find((freq) => freq.id === id)

      if (!updatedFrequency) {
        return
      }

      setFrequencies(updatedFrequencies)

      let frequency = updatedFrequency.frequency

      if (frequency) {
        frequency = (updatedFrequency.frequency_indicator || 'M') + frequency
      }

      await updateFrequency(updatedFrequency.id, {
        ...updatedFrequency,
        frequency,
      })
    } else if (type === 'equipment') {
      const updatedEquipments = equipments.map((equipment) =>
        id === equipment.id
          ? {
              ...equipment,
              [key]: value,
            }
          : equipment,
      )

      setEquipments(updatedEquipments)

      for (const equipment of updatedEquipments) {
        await updateEquipment(equipment.id, equipment)
      }
    } else if (type === 'freq group' && frequencyGroup) {
      const updatedFrequencyGroup = {
        ...frequencyGroup,
        [key]: value,
      }

      setFrequencyGroup(updatedFrequencyGroup)
      await updateFrequencyGroup(frequencyGroup.id, updatedFrequencyGroup)
    } else if (type === 'request') {
      await updateRequestStatus(id, value as string)
      setDetailedRequests((prev) =>
        prev.map((request) => (request.request_id === id ? { ...request, status: value as string } : request)),
      )
    }
  }

  const handleProcessAll = async () => {
    const results = await Promise.all(
      frequencies.map(async (freq) => {
        const result = await updateFrequency(freq.id, {
          ...freq,
          frequency: freq.frequency && (freq.frequency_indicator || 'M') + freq.frequency,
          status: 'ACCEPTED',
        })

        return result.success ? freq.id : null
      }),
    )

    const successfulIds = results.filter((id): id is number => id !== null)

    setFrequencies((prev) =>
      prev.map((elem) => (successfulIds.includes(elem.id) ? { ...elem, status: 'ACCEPTED' } : elem)),
    )
  }

  const getFrequency = async () => {
    const data = await getFrequencyGroup(request.frequency_group_id)

    if (data.data && data.success) {
      setFrequencyGroup(data.data)
    }
  }

  const getFrequencies = async () => {
    const body = await getFrequenciesByFrequencyGroupId(request.frequency_group_id)

    if (body.data && body.success) {
      const { data: equipments } = await getAllSFAFEquipments(filterOptions)
      const { data: stationClasses } = await getAllSFAFStationClasses(filterOptions)

      let defaultEmissionCodes: { [key: string]: string | null | undefined } = {}

      if (equipments && stationClasses) {
        defaultEmissionCodes = findEmissionCodeDefaultValue(equipments, body.data, stationClasses)
      }

      const formattedFrequencies = body.data.map((elem) => {
        const frequencyIndicator = elem.frequency ? elem.frequency.slice(0, 1) : 'M'

        const frequency = elem.frequency ? elem.frequency.slice(1) : elem.frequency

        const f_114 = elem.f_114 ? elem.f_114 : defaultEmissionCodes[elem.id] || ''

        return {
          ...elem,
          f_114,
          frequency,
          frequency_indicator: frequencyIndicator,
        }
      })

      setFrequencies(formattedFrequencies)
    }
  }

  const getEquipments = async () => {
    const data = await getEquipmentsByFrequencyGroupId(request.frequency_group_id)

    if (data.data && data.success) {
      setEquipments(data.data)
    }
  }

  const handleSFAFGenerate = async () => {
    const sfafAntennas = await getAllSFAFAntennas(filterOptions)
    const sfafContents = await getAllSFAFContents(filterOptions)
    const sfafEquipments = await getAllSFAFEquipments(filterOptions)
    const sfafFrequencies = await getAllSFAFFrequencies({
      ...filterOptions,
      requestFrequencyGroupId: request.frequency_group_id,
    })
    const sfafStationClasses = await getAllSFAFStationClasses(filterOptions)
    const sfafExcludedFrequencies = await getAllSFAFExcludedFrequencies(filterOptions)
    const sfafAgencyOverride = await getSfafAgencyOverride(Number(request.user_id))
    const hopsets = await getAllHopsets()

    if (
      sfafFrequencies.data &&
      sfafEquipments.data &&
      sfafContents.data &&
      sfafStationClasses.data &&
      sfafExcludedFrequencies.data &&
      sfafAntennas.data &&
      hopsets.data
    ) {
      const sfaf = await generateSFAF(
        sfafFrequencies.data,
        sfafContents.data,
        sfafExcludedFrequencies.data,
        sfafEquipments.data,
        hopsets.data,
        sfafStationClasses.data,
        sfafAntennas.data,
        sfafAgencyOverride.data,
      )

      setSfaf(sfaf)
    }
  }

  const base64ToBlob = (base64: string) => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: 'application/pdf' })
  }

  const downloadSinglePdf = async (subject: string) => {
    const data = await downloadPdf(
      subject,
      request.request_id,
      (request.site_location ?? '').split(',').map((str) => str.trim()),
      [new Date(request.start_date ?? 0).valueOf(), new Date(request.end_date ?? 0).valueOf()],
    )

    if (data && isValidBase64(data)) {
      const blob = base64ToBlob(data)

      const blobUrl = URL.createObjectURL(blob)

      if (pdfRef.current) {
        pdfRef.current.href = blobUrl
        pdfRef.current.click()
      }
    }
  }

  useEffect(() => {
    if (request.frequency_group_id) {
      getFrequency()
      getEquipments()
      getFrequencies()
    }
  }, [request])

  return (
    <Drawer
      className={styles.drawer}
      anchor="right"
      open={isOpen}
      onClose={onClose}
      classes={{
        paper: styles.drawerPaper,
      }}
    >
      <Box className={styles.drawerHeader}>
        <IconButton onClick={onClose}>
          <CloseIcon className={styles.closeIcon} />
        </IconButton>
      </Box>
      <Box className={styles.drawerContent}>
        <Box className={styles.freqStatusContainer}>
          <Box className={styles.fieldContainer}>
            <Typography>Request Status</Typography>
            <SelectComponent
              items={['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'TEST'].map((value) => ({
                value,
                displayValue: value,
              }))}
              defaultValue={request.status ?? undefined}
              className={styles.select}
              handleChange={(value) => handleChange('request', request.request_id, 'status', String(value))}
            />
          </Box>
          <Box className={styles.rfaExportContainer}>
            <Button variant="outlined" onClick={() => setIsExporting(true)}>
              RFA Export
            </Button>
          </Box>
        </Box>
        <Box className={styles.section}>
          <Box className={styles.titleContainer}>
            <Typography className={styles.title}>
              Assign Frequencies - {frequencies[0]?.start_indicator}
              {frequencies[0]?.start_freq}
              {'-'}
              {frequencies[0]?.end_indicator}
              {frequencies[0]?.end_freq}
            </Typography>
            <Button className={styles.btn} variant="outlined" onClick={handleProcessAll}>
              Process All
            </Button>
          </Box>
          {frequencies.map((freq) => (
            <Box className={styles.assignedFreq} key={freq.id}>
              <TextField
                className={styles.input}
                placeholder="Override Id"
                value={freq.id_override ?? ''}
                onChange={(evt) => handleChange('freq', freq.id, 'id_override', evt.target.value)}
              />
              <OutlinedInput
                className={styles.frequencyInput}
                placeholder="frequency"
                value={freq.frequency ?? ''}
                onChange={(evt) => handleChange('freq', freq.id, 'frequency', evt.target.value)}
                endAdornment={
                  <Select
                    sx={{
                      boxShadow: 'none',
                      fontSIze: 12,

                      '.MuiOutlinedInput-notchedOutline': {
                        border: 0,
                        padding: '16.5px 9px',
                      },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        border: 0,
                      },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 0,
                      },
                    }}
                    className={styles.select}
                    value={freq.frequency_indicator || 'M'}
                    onChange={(evt) => handleChange('freq', freq.id, 'frequency_indicator', evt.target.value)}
                  >
                    <MenuItem value="H" className={styles.option}>
                      Hz
                    </MenuItem>
                    <MenuItem value="K" className={styles.option}>
                      KHz
                    </MenuItem>
                    <MenuItem value="M" className={styles.option}>
                      MHz
                    </MenuItem>
                    <MenuItem value="G" className={styles.option}>
                      GHz
                    </MenuItem>
                  </Select>
                }
              />
              <TextField
                placeholder="Serial number"
                className={styles.input}
                defaultValue={freq.list_serial_number}
                onChange={(evt) => handleChange('freq', freq.id, 'list_serial_number', evt.target.value)}
              />
              <TextField
                className={styles.input}
                placeholder="F_114"
                defaultValue={freq.f_114}
                onChange={(evt) => handleChange('freq', freq.id, 'f_114', evt.target.value)}
              />
              <SelectComponent
                items={['NOT_STARTED', 'ACCEPTED', 'APPROVED', 'DENIED'].map((value) => ({
                  value,
                  displayValue: value,
                }))}
                value={freq.status}
                className={styles.select}
                handleChange={(value) => handleChange('freq', freq.id, 'status', String(value))}
              />
            </Box>
          ))}
        </Box>
        <Box className={styles.section}>
          <Box className={styles.titleContainer}>
            <Typography className={styles.title}>Override/Admin Entries</Typography>
          </Box>
          {equipments.map((equipment) => (
            <Box className={styles.equipment} key={equipment.id}>
              <Box className={styles.titleContainer}>
                <Typography>340: {equipment.equipment_name}</Typography>
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography>343</Typography>
                <TextField
                  defaultValue={equipment.f_343}
                  placeholder="F_343"
                  onChange={(evt) => handleChange('equipment', equipment.id, 'f_343', evt.target.value)}
                />
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography>501</Typography>
                <Box className={styles.f_501}>
                  <TextField
                    defaultValue={equipment.f_501_irac}
                    placeholder="F_501 IRAC"
                    onChange={(evt) => handleChange('equipment', equipment.id, 'f_501_irac', evt.target.value)}
                  />
                  <TextField
                    defaultValue={equipment.f_501_sps}
                    placeholder="F_501 SPS"
                    onChange={(evt) => handleChange('equipment', equipment.id, 'f_501_sps', evt.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {frequencyGroup && (
          <>
            <Box className={styles.antennaContainer}>
              <Typography>Antenna:</Typography>
              <Box className={styles.fields}>
                <TextField
                  placeholder="F_354"
                  defaultValue={frequencyGroup.f_354}
                  onChange={(evt) => handleChange('freq group', frequencyGroup.id, 'f_354', evt.target.value)}
                />
                <TextField
                  placeholder="F_454"
                  defaultValue={frequencyGroup.f_454}
                  onChange={(evt) => handleChange('freq group', frequencyGroup.id, 'f_454', evt.target.value)}
                />
              </Box>
            </Box>
            <Box className={styles.orientationContainer}>
              <Typography>Orientation:</Typography>
              <Box className={styles.fields}>
                <TextField
                  defaultValue={frequencyGroup.f_362}
                  placeholder="F_362"
                  onChange={(evt) => handleChange('freq group', frequencyGroup.id, 'f_362', evt.target.value)}
                />
                <TextField
                  defaultValue={frequencyGroup.f_462}
                  placeholder="F_462"
                  onChange={(evt) => handleChange('freq group', frequencyGroup.id, 'f_462', evt.target.value)}
                />
              </Box>
            </Box>
          </>
        )}
        <Box className={styles.actions}>
          <Button className={styles.generateBtn} variant="outlined" onClick={() => handleSFAFGenerate()}>
            Generate SFAF
          </Button>
          <Button className={styles.exportBtn} variant="outlined" onClick={handleExport}>
            Export SFAF
          </Button>
        </Box>
        <Box className={styles.sfafContainer}>
          <textarea
            placeholder="SFAF value"
            style={{
              fontSize: 14,
              width: '100%',
              height: sfaf ? '1000px' : '50px',
              color: theme.palette.text.primary,
              background: 'none',
              padding: '5px',
            }}
            value={sfaf}
            readOnly={true}
          />
        </Box>
      </Box>

      <a ref={linkRef} hidden />
      <a ref={pdfRef} download hidden />
      <RFAExportModal handleConfirm={downloadSinglePdf} open={isExporting} onClose={() => setIsExporting(false)} />
    </Drawer>
  )
}

export default SFAFDrawer
